import { useRef, useState } from 'react';
import { useResultPhotoMenuEffect } from '../../../modules/VehiculeCheckResult/hooks/result-image.hooks';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { ImgWithYoloBox } from '@/components/Custom/Photo/ImgWithYoloBox';
import { twMerge } from 'tailwind-merge';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { TFunction } from 'i18next';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { Trans } from 'react-i18next';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { MenuSeparator } from '@/components/Menu/MenuSeparator';

interface PhotoWithMenuProps {
  t?: TFunction<undefined>;
  url?: string;
  urlHasBaseUrl?: boolean;
  name?: string | React.ReactNode;
  timeInVideo?: string;
  detectionCoordinates?: TResultImageCoordinatesSchema;
  manyRectangleAnnotations?: TResultImageCoordinatesSchema[];
  manyPolygonAnnotations?: TResultImageCoordinatesSchema[];
  originalBoundingBox?: Array<number>;
  activateEngineCorrection?: boolean;
  hideActionButtons?: boolean;
  showInfoWithinImage?: boolean;
  photoNameClassName?: string;
  containerClassName?: string;
  imageClassName?: string;
  polygonClassName?: string;
  isModerator?: boolean;
  navigationChildren?: React.ReactNode;
  onViewText?: string;
  onCanvasImageLoading?: (loading: boolean) => void;
  onClickImage?: () => void;
  onClickAdd?: () => void;
  onClickEdit?: () => void;
  onClickDeactivateSingleImage?: () => void;
  onClickDeactivateAllImages?: () => void;
  onClickView?: () => void;
  onClickModerate?: () => void;
  onClickEditModeration?: () => void;
  onClickDeactivateModeration?: () => void;
}

export const PhotoWithMenu = ({
  t,
  url,
  urlHasBaseUrl = false,
  name,
  timeInVideo,
  activateEngineCorrection = false,
  hideActionButtons,
  showInfoWithinImage = false,
  photoNameClassName,
  containerClassName,
  detectionCoordinates,
  manyRectangleAnnotations,
  manyPolygonAnnotations,
  originalBoundingBox,
  imageClassName,
  polygonClassName,
  navigationChildren,
  onViewText,
  onCanvasImageLoading,
  onClickImage,
  onClickAdd,
  onClickEdit,
  onClickDeactivateSingleImage,
  onClickDeactivateAllImages,
  onClickView,
  onClickModerate,
  onClickEditModeration,
  onClickDeactivateModeration,
}: PhotoWithMenuProps) => {
  const imageUrl = urlHasBaseUrl ? url : `${R2_PUBLIC_DOMAIN}/${url}`;
  const menuRef = useRef<HTMLDivElement>(null);

  const [isImageLoading, setIsImageLoading] = useState(true);

  const { isPhotoHovered, isMenuOpen, setIsPhotoHovered, setIsMenuOpen } =
    useResultPhotoMenuEffect(menuRef);

  const renderImageInfos = () => {
    return (
      <div
        className={twMerge(
          '',
          showInfoWithinImage &&
            'absolute bottom-0 left-0 w-full bg-black/50 p-2',
        )}
      >
        {name && (
          <div
            className={twMerge(
              'mt-2 text-sm font-medium text-slate-600 text-center',
              photoNameClassName,
              showInfoWithinImage && 'mt-0 font-normal text-white text-start',
            )}
          >
            {name}
          </div>
        )}
        {timeInVideo && (
          <div
            className={twMerge(
              'font-medium text-center text-[var(--tw-gray-600)] text-2sm',
              showInfoWithinImage && 'font-normal text-white',
            )}
          >
            {t('common:video')} : {`[${timeInVideo}]`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={twMerge(
        'flex select-none group/result-photo',
        containerClassName,
      )}
      onMouseEnter={() => {
        setIsPhotoHovered(true);
      }}
      onMouseLeave={() => {
        setIsPhotoHovered(false);
      }}
    >
      <div
        className={twMerge(
          'w-[88%] max-md:w-full',
          hideActionButtons && 'w-full',
        )}
      >
        <div className="relative aspect-square bg-blue-131025 rounded-xl select-none overflow-hidden">
          <ImgWithYoloBox
            imgSrc={imageUrl}
            onImageLoading={(loading) => {
              setIsImageLoading(loading);
              onCanvasImageLoading?.(loading);
            }}
            imageClassName={twMerge('hover:cursor-pointer', imageClassName)}
            polygonClassName={polygonClassName}
            element={detectionCoordinates}
            originalBoundingBox={originalBoundingBox}
            polygonThickness={9}
            manyRectangleAnnotations={manyRectangleAnnotations}
            manyPolygonAnnotations={manyPolygonAnnotations}
            onClick={onClickImage}
          />
          {showInfoWithinImage ? renderImageInfos() : navigationChildren}
        </div>
        {!showInfoWithinImage && renderImageInfos()}
      </div>
      {!hideActionButtons && (
        <div className="relative max-h-[261px] min-w-6 flex-col max-sm:hidden">
          <div
            ref={menuRef}
            className={twMerge(
              'absolute top-2 left-1.75 hidden',
              (isPhotoHovered || isMenuOpen) && 'block',
            )}
          >
            <TableMenu
              menuClassName="w-auto"
              menuPosition="bottom-start"
              triggerIcon={
                <IconAdjustmentsHorizontal
                  className="text-gray-600"
                  size={26.5}
                  stroke={1.75}
                />
              }
              disabled={isImageLoading}
              onClickTrigger={() => {
                setIsMenuOpen((prevState) => !prevState);
              }}
            >
              {onClickAdd && (
                <SubMenuItem
                  itemClassName="!mx-1.75 !py-[3.5px]"
                  leftIconClassName="ki-plus-squared font-semibold !text-[18.5px]"
                  textClassName="mt-0.5"
                  text={t('common:addImage')}
                  onClick={() => {
                    onClickAdd?.();
                  }}
                />
              )}
              {onClickEdit && (
                <SubMenuItem
                  itemClassName="!mx-1.75 !py-[3.5px]"
                  leftIconClassName="ki-notepad-edit font-semibold !text-[17.5px]"
                  textClassName="mt-0.5"
                  text={t('common:modifyThisImage')}
                  onClick={() => {
                    onClickEdit?.();
                  }}
                />
              )}
              {(onClickDeactivateSingleImage || onClickDeactivateAllImages) && (
                <MenuSeparator />
              )}
              {onClickDeactivateSingleImage && (
                <SubMenuItem
                  itemClassName="!mx-1.75 !py-[3.5px]"
                  leftIconClassName="ki-cross font-semibold !text-[19.5px]"
                  textClassName="mt-0.5"
                  text={t('common:deleteThisImage')}
                  onClick={() => {
                    onClickDeactivateSingleImage?.();
                  }}
                />
              )}
              {onClickDeactivateAllImages && (
                <SubMenuItem
                  itemClassName="!mx-1.75 !py-[3.5px]"
                  leftIconClassName="ki-trash font-semibold !text-[19.5px]"
                  textClassName="mt-0.5"
                  text={t('common:deleteAllImages')}
                  onClick={() => {
                    onClickDeactivateAllImages?.();
                  }}
                />
              )}
              {onClickView && (
                <SubMenuItem
                  itemClassName="!mx-1.75 !py-[3.5px]"
                  leftIconClassName="ki-eye font-semibold !text-[17.5px]"
                  textClassName="mt-0.5"
                  text={onViewText || t('common:view')}
                  onClick={() => {
                    onClickView?.();
                  }}
                />
              )}
              {onClickModerate && (
                <SubMenuItem
                  itemClassName="!mx-1.75 !py-[3.5px]"
                  leftIconClassName="ki-toggle-on font-semibold !text-[17.5px]"
                  textClassName="mt-0.5"
                  text={t('common:moderateThisImage')}
                  onClick={() => {
                    onClickModerate?.();
                  }}
                />
              )}
              {activateEngineCorrection && (
                <>
                  <MenuSeparator />
                  <SubMenuItem
                    itemClassName={twMerge(
                      '!mx-1.75 !py-[3.5px]',
                      detectionCoordinates && '!items-start',
                    )}
                    leftIconClassName={twMerge(
                      'ki-toggle-on font-semibold !text-[19.3px]',
                      detectionCoordinates && 'mt-1',
                    )}
                    textClassName="mt-0.5"
                    text={
                      detectionCoordinates ? (
                        <Trans
                          i18nKey={t('common:modifyThisImageModeration')}
                          components={{
                            br: <br />,
                            div: <div className="leading-tight py-1" />,
                          }}
                        />
                      ) : (
                        t('common:moderateThisImage')
                      )
                    }
                    onClick={() => {
                      onClickEditModeration?.();
                    }}
                  />
                  {detectionCoordinates && (
                    <SubMenuItem
                      itemClassName="!mx-1.75 !py-[3.5px] items-start"
                      leftIconClassName="mt-1 ki-cross-square font-semibold !text-[19.5px]"
                      textClassName="mt-0.5"
                      text={
                        <Trans
                          i18nKey={t('common:deleteThisImageModeration')}
                          components={{
                            br: <br />,
                            div: <div className="leading-tight py-1" />,
                          }}
                        />
                      }
                      onClick={() => {
                        onClickDeactivateModeration?.();
                      }}
                    />
                  )}
                </>
              )}
            </TableMenu>
          </div>
        </div>
      )}
    </div>
  );
};
